/**
 * ResultRiskBanner — compact, muted risk/legal disclaimer shown alongside
 * backtest, aggregation, and paper-trading result views (SHA-12).
 *
 * Rendered once inside <Dashboard>, which is the single shared metrics
 * component used by:
 *   - App.tsx (backtest + aggregation result views — unified engine, so one
 *     Dashboard instance covers both)
 *   - PaperMetricsDashboard.tsx (paper-trading session result view — also
 *     used for LIVE (real-money) sessions, see `isLive` below)
 *
 * Kept intentionally small and visually unobtrusive — informational, not
 * alarming. Full legal terms live in the Terms of Service (/terms) and
 * Privacy Policy (/privacy) pages.
 *
 * SHA-72: the disclaimer text says results are "hypothetical" / "simulated
 * (paper/backtest)" — that is true for backtests and paper-trading sessions,
 * but it is factually WRONG for a LIVE session (real money, real fills via a
 * live connector such as Bybit). Callers that know the session is live MUST
 * pass `isLive` explicitly so the banner is suppressed there; the default
 * (`isLive` omitted/false) keeps showing the disclaimer, which is correct for
 * every other current caller (backtest/aggregation/paper).
 */

interface ResultRiskBannerProps {
  /** True for a real-money live session (e.g. `connectorType === 'bybit'`). */
  isLive?: boolean;
}

export function ResultRiskBanner({ isLive = false }: ResultRiskBannerProps = {}) {
  if (isLive) return null;

  return (
    <p role="note" className="text-xs text-gray-500 leading-snug">
      Hypothetical results — for research/education only, not financial advice. Past
      performance does not guarantee future results. Simulated (paper/backtest)
      performance has inherent limitations and may differ materially from live trading.
    </p>
  );
}
